import { Tooltip } from '@/design-system';
import GithubIcon from '@/icons/GithubIcon.svg?react';
import { cn } from '@/utils/tailwind';

import { getCommitUrlFromRemoteUrl } from '../utils/getCommitFromRemoteUrl';
import { TextOverflowTooltip } from './TextOverflowTooltip';

type GithubChipProps = {
  gitMetadata: unknown;
  className?: string;
  fallbackText?: string;
};

const getGithubChipText = (
  repoName: string,
  commit: string
): React.ReactNode => {
  const shortCommit = commit.slice(0, 6);
  if (repoName.length > 6) {
    const shortRepoName = `${repoName.slice(0, 6)}...`;
    return (
      <Tooltip title={`${repoName}/${shortCommit}`}>
        <div className="truncate">{`${shortRepoName}/${shortCommit}`}</div>
      </Tooltip>
    );
  }

  return (
    <TextOverflowTooltip>{`${repoName}/${shortCommit}`}</TextOverflowTooltip>
  );
};

export function GithubChip(props: GithubChipProps) {
  const { gitMetadata, className, fallbackText } = props;

  const isValid = typeof gitMetadata === 'object' && gitMetadata;

  const url =
    isValid &&
    'remote_url' in gitMetadata &&
    'commit' in gitMetadata &&
    typeof gitMetadata.remote_url === 'string' &&
    typeof gitMetadata.commit === 'string'
      ? getCommitUrlFromRemoteUrl(gitMetadata.remote_url, gitMetadata.commit)
      : null;

  return isValid &&
    url &&
    'repo_name' in gitMetadata &&
    'commit' in gitMetadata ? (
    <div
      role="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(url, '_blank');
      }}
      className={cn(
        className,
        'flex max-w-[150px] items-center justify-start gap-1 rounded-2xl border border-secondary bg-background px-2 py-0.5'
      )}
    >
      <GithubIcon className="h-4 w-4 flex-shrink-0" />
      {typeof gitMetadata.repo_name === 'string' &&
        typeof gitMetadata.commit === 'string' &&
        getGithubChipText(gitMetadata.repo_name, gitMetadata.commit)}
    </div>
  ) : fallbackText ? (
    <div className="overflow-auto py-3 no-scrollbar">{fallbackText}</div>
  ) : null;
}
